/**
 * Appointment email service – sends admin + client emails via Firebase Cloud Function (Resend + reCAPTCHA).
 */

import { emailConfig } from './emailConfig';
import { processEmailTemplate } from './emailTemplateService';

interface SingleEmailPayload {
  to: { email: string; name?: string };
  subject: string;
  htmlContent: string;
  sender?: { name: string; email: string };
}

interface BatchEmailResult {
  success: boolean;
  results?: Array<{ success: boolean; messageId?: string; error?: string }>;
  summary?: { total: number; sent: number; failed: number };
  error?: string;
}

export interface AppointmentFormData {
  name: string;
  email: string;
  phone: string;
  preferredDate: string;
  specialist: string;
  subject?: string;
  message?: string;
}

/**
 * Get reCAPTCHA token from the page widget.
 */
function getRecaptchaToken(): string {
  try {
    if (typeof (window as any).grecaptcha !== 'undefined') {
      const token = (window as any).grecaptcha.getResponse();
      if (token) return token;
    }
    const textarea = document.querySelector('#g-recaptcha-response') as HTMLTextAreaElement;
    if (textarea?.value) return textarea.value;
  } catch (e) {
    console.error('Error getting reCAPTCHA token:', e);
  }
  return '';
}

/**
 * Send batch of emails via Cloud Function.
 */
async function sendBatchEmails(emails: SingleEmailPayload[], recaptchaToken?: string): Promise<BatchEmailResult> {
  try {
    if (!emailConfig.CLOUD_FUNCTION_URL) {
      throw new Error('Cloud Function URL not configured. Check REACT_APP_FIREBASE_PROJECT_ID.');
    }

    const token = recaptchaToken || getRecaptchaToken();
    if (!token) {
      throw new Error('reCAPTCHA não concluído. Por favor complete a verificação.');
    }

    const emailsWithSender = emails.map((email) => ({
      ...email,
      sender: email.sender ?? {
        name: emailConfig.FROM_NAME,
        email: emailConfig.FROM_EMAIL,
      },
    }));

    const payload = { emails: emailsWithSender, recaptchaToken: token };

    const fetchOptions: RequestInit = {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    };

    // keepalive: lets the request survive tab close. Limit is 64KB.
    // If payload exceeds the limit, fetch throws synchronously → fall back without keepalive.
    let response: Response;
    try {
      response = await fetch(emailConfig.CLOUD_FUNCTION_URL, { ...fetchOptions, keepalive: true });
    } catch (err: any) {
      console.warn('keepalive fetch failed, retrying without keepalive:', err?.message);
      response = await fetch(emailConfig.CLOUD_FUNCTION_URL, fetchOptions);
    }

    const text = await response.text();
    if (!response.ok) {
      console.error('Cloud Function error:', response.status, text);
      throw new Error(`Erro ao enviar: ${response.status}`);
    }

    let result;
    try {
      result = JSON.parse(text);
    } catch {
      result = { success: true };
    }

    return {
      success: result.success,
      results: result.results,
      summary: result.summary,
    };
  } catch (err) {
    console.error('Error sending emails:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Erro desconhecido',
    };
  }
}

/**
 * Build admin notification email payload.
 */
async function prepareAdminEmail(formData: AppointmentFormData): Promise<SingleEmailPayload | null> {
  try {
    const timestamp = new Date().toLocaleString('pt-PT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    const messageBlock = formData.message
      ? `<div style="background-color: #fafafa; border-left: 4px solid #1977cc; padding: 20px; margin-bottom: 20px; border-radius: 0 8px 8px 0;">
           <h3 style="color: #1977cc; margin: 0 0 14px 0; font-size: 15px; font-weight: 600;">Mensagem</h3>
           <p style="margin: 0; color: #555; font-size: 14px; line-height: 1.65; white-space: pre-wrap;">${formData.message}</p>
         </div>`
      : '';

    const variables: Record<string, string> = {
      patient_name: formData.name || '',
      email: formData.email || '',
      phone: formData.phone || '',
      preferred_date: formData.preferredDate || 'A definir',
      specialist: formData.specialist || 'Dr. Guilherme Galvão',
      subject: formData.subject || 'Sessão Integrativa',
      message_block: messageBlock,
      timestamp,
      logo_url: emailConfig.LOGO_URL,
    };

    const htmlContent = await processEmailTemplate('appointment-admin.html', variables);
    const subject = `📩 Nova marcação: ${formData.name || 'Site'}`;

    return {
      to: { email: emailConfig.TO_EMAIL, name: 'Clínica Dr. Guilherme Galvão' },
      subject,
      htmlContent,
    };
  } catch (e) {
    console.error('Error preparing admin email:', e);
    return null;
  }
}

/**
 * Build client confirmation email payload.
 */
async function prepareUserEmail(formData: AppointmentFormData): Promise<SingleEmailPayload | null> {
  try {
    if (!formData.email) return null;

    const variables: Record<string, string> = {
      patient_name: formData.name || '',
      email: formData.email || '',
      phone: formData.phone || '',
      preferred_date: formData.preferredDate || 'A definir',
      year: new Date().getFullYear().toString(),
      logo_url: emailConfig.LOGO_URL,
    };

    const htmlContent = await processEmailTemplate('appointment-user.html', variables);
    const subject = 'Confirmação de marcação – Clínica Dr. Guilherme Galvão';

    return {
      to: { email: formData.email, name: formData.name || '' },
      subject,
      htmlContent,
    };
  } catch (e) {
    console.error('Error preparing user email:', e);
    return null;
  }
}

/**
 * Send both admin and client emails in one batch.
 */
export async function sendAppointmentEmails(
  formData: AppointmentFormData,
  recaptchaToken?: string
): Promise<BatchEmailResult> {
  const emails: SingleEmailPayload[] = [];

  const adminEmail = await prepareAdminEmail(formData);
  if (adminEmail) emails.push(adminEmail);

  const userEmail = await prepareUserEmail(formData);
  if (userEmail) emails.push(userEmail);

  if (emails.length === 0) {
    return { success: false, error: 'Nenhum email a enviar.' };
  }

  return sendBatchEmails(emails, recaptchaToken);
}
